// Operational status values of the `status` tag, and how the update dialog shows them.

export const OPERATIONAL = "operational";
export const NON_OPERATIONAL = "non_operational";
// Set by inventory onboarding for new vehicles; only some orgs have it as a status value.
export const ONBOARDING_IN_PROGRESS = "onboarding_inprogress";

const STATUS_DISPLAY = {
    [OPERATIONAL]: { label: "Operational", color: "#008A15" },
    [NON_OPERATIONAL]: { label: "Non-Operational", color: "#CC4E01" },
    [ONBOARDING_IN_PROGRESS]: { label: "Onboarding In Progress", color: "#1B3FA0" },
};

/**
 * Statuses the user can pick: Operational and Non-Operational always, plus
 * Onboarding In Progress only when the org's `status` tag supports all three
 * values. Labels are fixed here, not the tag display names (onboarding's is
 * stored lower case).
 *
 * @param {Array} orgTags - /api/v1/available-tags tags ({ tagCode, supportedValues })
 * @returns {Array<{ value: string, label: string }>}
 */
export const getStatusOptions = (orgTags = []) => {
    const statusTag = (orgTags || []).find((tag) => tag?.tagCode === "status");
    const supported = new Set((statusTag?.supportedValues || []).map((value) => value?.valueCode));
    const values = [OPERATIONAL, NON_OPERATIONAL];
    if ([OPERATIONAL, NON_OPERATIONAL, ONBOARDING_IN_PROGRESS].every((value) => supported.has(value))) {
        values.push(ONBOARDING_IN_PROGRESS);
    }
    return values.map((value) => ({ value, label: STATUS_DISPLAY[value].label }));
};

/**
 * Whether the dialog updates one entity. Select-all is always a bulk update, even
 * when the current page holds one row: entityIds only lists the rows on the page.
 */
export const isSingleEntityUpdate = ({ isSelectAll = false, entityIds = [] } = {}) =>
    !isSelectAll && (entityIds || []).length === 1;

/**
 * Loads the dialog's entity tags and the org's status options independently, so a
 * failed entity fetch does not stop the status options and non-operational reasons
 * from loading (and the reverse). Never rejects; failures go to onError.
 *
 * @param {object} args
 * @param {Function} args.loadEntityTags    - fetches and stores the selected entities' tags
 * @param {Function} args.loadStatusOptions - fetches and stores the org's available tags
 * @param {Function} [args.onError]         - (message, error) => void
 */
export const loadStatusDialogData = ({ loadEntityTags, loadStatusOptions, onError = console.error }) => {
    const run = (load, message) => Promise.resolve()
        .then(load)
        .catch((err) => onError(message, err));
    return Promise.all([
        run(loadEntityTags, "Failed to load operational status:"),
        run(loadStatusOptions, "Failed to load status options:"),
    ]);
};

const SAVE_SUCCESS_CODES = [200, 201];
// GetData's SetSessionExpired() code, returned for a failed token refresh or an HTTP 500.
const SESSION_EXPIRED_CODE = -2;
const SAVE_ERROR_FALLBACK = "Update failed. Please try again.";

/**
 * Outcome of a tag save. GetData never throws on an HTTP or API error: it resolves
 * with a non-success responseStatus.code, so the code is what tells a save apart.
 *
 * Session expiry is flagged separately: callers hand it to the session-expired flow,
 * like every other GetData caller, instead of showing its "session expired" text.
 *
 * @param {object} res - updateEntityTags / bulkAssignTags result
 * @returns {{ ok: boolean, sessionExpired: boolean, message: string|null }} message is the error toast text
 */
export const getTagSaveResult = (res) => {
    const code = res?.responseStatus?.code;
    if (SAVE_SUCCESS_CODES.includes(code)) {
        return { ok: true, sessionExpired: false, message: null };
    }
    if (code === SESSION_EXPIRED_CODE) {
        return { ok: false, sessionExpired: true, message: SAVE_ERROR_FALLBACK };
    }
    const msg = res?.responseStatus?.msg;
    return {
        ok: false,
        sessionExpired: false,
        message: typeof msg === "string" && msg.trim() ? msg : SAVE_ERROR_FALLBACK,
    };
};

/** { label, color } for a status value; an unknown or empty status is "-" with no colour. */
export const getStatusDisplay = (status) => STATUS_DISPLAY[status] || { label: "-", color: "" };

/**
 * Current-status breakdown for the bulk dialog. Entities with no status tag, and
 * selected entities the tags response does not mention, count as not set; a status
 * value this dialog does not know counts as other.
 *
 * @param {object} tagsByEntity  - { [entityID]: [{ tagCode, valueCode }] }
 * @param {number} selectedCount - how many entities the user selected
 */
export const countStatuses = (tagsByEntity = {}, selectedCount = 0) => {
    const counts = {
        [OPERATIONAL]: 0,
        [NON_OPERATIONAL]: 0,
        [ONBOARDING_IN_PROGRESS]: 0,
        other: 0,
        not_set: 0,
        total: 0,
    };
    const entityIDs = Object.keys(tagsByEntity || {});
    entityIDs.forEach((entityID) => {
        const statusTag = (tagsByEntity[entityID] || []).find((tag) => tag?.tagCode === "status");
        const valueCode = statusTag?.valueCode;
        if (!valueCode) {
            counts.not_set += 1;
        } else if (STATUS_DISPLAY[valueCode]) {
            counts[valueCode] += 1;
        } else {
            counts.other += 1;
        }
        counts.total += 1;
    });
    if (entityIDs.length < selectedCount) {
        const missing = selectedCount - entityIDs.length;
        counts.not_set += missing;
        counts.total += missing;
    }
    return counts;
};

/**
 * Errors for an update that would change nothing, keyed as the dialog shows them:
 * noChangeInOperationalStatus, noChangeInReasonForNonOperational.
 *
 * Single entity: the same status is refused, except Non-Operational, which may change
 * its reason. Bulk: refused only when every selected entity already has the status
 * (for Non-Operational, also the same reason).
 *
 * @param {object} args
 * @param {boolean} args.isSingleEntity
 * @param {string}  args.newStatus
 * @param {string}  [args.newReason]        - sub_status value for Non-Operational
 * @param {object}  [args.singleEntityData] - { status, reasonForNonOperational }
 * @param {object}  [args.bulkCounts]       - countStatuses() result
 * @param {object}  [args.bulkTags]         - { [entityID]: tags } behind bulkCounts
 */
export const getNoChangeErrors = ({
    isSingleEntity,
    newStatus,
    newReason,
    singleEntityData = {},
    bulkCounts = {},
    bulkTags = {},
}) => {
    const errors = {};
    if (!newStatus) return errors;

    if (isSingleEntity) {
        if (singleEntityData.status !== newStatus) return errors;
        if (newStatus !== NON_OPERATIONAL) {
            errors.noChangeInOperationalStatus = true;
        } else if (singleEntityData.reasonForNonOperational === newReason) {
            errors.noChangeInReasonForNonOperational = true;
        }
        return errors;
    }

    const total = bulkCounts.total || 0;
    if (!total || bulkCounts[newStatus] !== total) return errors;
    if (newStatus !== NON_OPERATIONAL) {
        errors.noChangeInOperationalStatus = true;
        return errors;
    }
    const reasons = new Set(Object.values(bulkTags || {}).map(
        (tags) => (tags || []).find((tag) => tag?.tagCode === "sub_status")?.valueCode
    ));
    if (reasons.size === 1 && reasons.has(newReason)) {
        errors.noChangeInReasonForNonOperational = true;
    }
    return errors;
};
